import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { useI18n } from '../i18n/LanguageContext';
import { Reveal } from '../components/Reveal';

const IMG = {
  // Workshop history — close-up patterned printing cylinder with ink residue
  history: 'https://images.unsplash.com/photo-1699662585180-8cd17dfa9195?auto=format&fit=crop&w=1800&q=80',
  // Machinery — silk screen printing frame held by hand
  machinery: 'https://images.unsplash.com/photo-1456456496250-d5e7c0a9b44d?auto=format&fit=crop&w=1800&q=80',
  // Facilities — textile rotary printer with technician
  factory: 'https://images.unsplash.com/photo-1584269613077-1e64a0ac2779?auto=format&fit=crop&w=1800&q=80',
};

export default function About() {
  const { t } = useI18n();
  return (
    <div data-testid="about-page">
      {/* Hero - text-heavy */}
      <section className="container-x pt-24 md:pt-36 pb-16" data-testid="about-hero">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <Reveal className="md:col-span-8">
            <div className="kicker">{t.about.kicker}</div>
            <h1 className="display-1 mt-6 text-forest text-balance">{t.about.title}</h1>
          </Reveal>
          <Reveal delay={100} className="md:col-span-4 md:col-start-9">
            <p className="text-[11px] uppercase tracking-[0.22em] text-forest/45">— 1981 / Vilassar de Dalt</p>
          </Reveal>
        </div>
      </section>

      <section className="container-x grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-20 pb-24">
        <Reveal className="md:col-span-7 relative overflow-hidden">
          <img src={IMG.history} alt="Workshop history" className="w-full h-[560px] object-cover" />
        </Reveal>
        <Reveal delay={120} className="md:col-span-5 flex flex-col justify-center">
          <p className="lead">{t.about.intro}</p>
        </Reveal>
      </section>

      {/* Timeline */}
      <section className="bg-sand-warm" data-testid="timeline">
        <div className="container-x py-24 md:py-32">
          <Reveal>
            <div className="kicker">— Timeline</div>
            <h2 className="display-2 mt-6 text-forest max-w-3xl text-balance">{t.about.timelineTitle}</h2>
          </Reveal>
          <div className="mt-16 divide-y divide-forest/15 border-y border-forest/15">
            {t.about.timeline.map((row, i) => (
              <Reveal key={i} delay={i * 60} className="grid grid-cols-12 gap-6 py-10">
                <div className="col-span-12 md:col-span-2 font-serif text-3xl md:text-4xl font-light text-forest">
                  {row.year}
                </div>
                <div className="col-span-12 md:col-span-4">
                  <h3 className="font-serif text-xl md:text-2xl font-light text-forest">{row.title}</h3>
                </div>
                <div className="col-span-12 md:col-span-6">
                  <p className="body-text">{row.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Facilities */}
      <section className="container-x py-24 md:py-32" data-testid="facilities">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-20 items-center">
          <Reveal className="md:col-span-5 order-2 md:order-1">
            <div className="kicker">{t.about.facilitiesKicker}</div>
            <h2 className="display-2 mt-6 text-forest text-balance">{t.about.facilitiesTitle}</h2>
            <p className="lead mt-8">{t.about.facilitiesBody}</p>
            <Link to="/servicios" className="mt-8 inline-flex items-center gap-2 text-forest link-underline text-[12px] uppercase tracking-[0.22em]" data-testid="about-cta-services">
              {t.cta.ourServices} <ArrowUpRight size={14} />
            </Link>
          </Reveal>
          <Reveal delay={100} className="md:col-span-7 order-1 md:order-2">
            <img src={IMG.factory} alt="Facilities" className="w-full h-[480px] md:h-[560px] object-cover" />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
